import { motion } from "framer-motion";
import { 
  sectionVariant, 
  staggerContainer, 
  itemVariant,
  viewportOnce,
  viewportOnceNear,
  cardHover
} from "@/lib/motion";
import { resolveIcon } from "@/lib/iconResolver";

export interface WhoItsForContent {
  eyebrow: string;
  headline: string;
  description: string;
  notForNote: string;
  collaborationNote: string;
  audiences: {
    icon: string;
    stage: string;
    title: string;
    description: string;
    notFor: string;
  }[];
}

export const whoItsForDefaults: WhoItsForContent = {
  eyebrow: "Who This Is For",
  headline: "Built for teams ready to think in systems.",
  description: "We partner with companies at different stages. Each with distinct challenges that require the same rigor.",
  notForNote: "Not for everyone. If you're seeking a vendor to execute tasks, we're not the right fit.",
  collaborationNote: "We work alongside your team. The goal is to strengthen internal capability, not replace it.",
  audiences: [
    {
      icon: "Rocket",
      stage: "0 → 1",
      title: "Startups",
      description: "Finding what works before runway runs out. We validate channels and build acquisition engines from day one.",
      notFor: "Not for: brands chasing vanity metrics",
    },
    {
      icon: "TrendingUp",
      stage: "1 → 10",
      title: "Growth Companies",
      description: "Scaling without breaking economics. Optimize channels, expand reach, build systems that sustain momentum.",
      notFor: "Not for: companies avoiding measurement",
    },
    {
      icon: "Building2",
      stage: "10 → 100",
      title: "Scale-ups",
      description: "Maintaining efficiency at volume. Strategic partnership, integrated thinking across your organization.",
      notFor: "Not for: brands wanting a vendor, not a partner",
    },
  ],
};

interface WhoItsForProps {
  content?: WhoItsForContent;
}

const WhoItsFor = ({ content: c = whoItsForDefaults }: WhoItsForProps) => {
  return (
    <section className="py-12 md:py-16 lg:py-20 border-t border-border">
      <div className="container-wide">
        <motion.div 
          className="max-w-2xl mb-8 md:mb-10 lg:mb-12"
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          variants={sectionVariant}
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-px bg-primary" />
            <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium">
              {c.eyebrow}
            </span>
          </div>
          <h2 className="font-display text-2xl md:text-3xl lg:text-4xl font-semibold tracking-tight mb-5">
            {c.headline}
          </h2>
          <p className="text-muted-foreground leading-relaxed max-w-lg mb-4">
            {c.description}
          </p>
          <p className="text-xs text-muted-foreground/60 max-w-md mb-2">
            {c.notForNote}
          </p>
          <p className="text-xs text-muted-foreground/50 max-w-md">
            {c.collaborationNote}
          </p>
        </motion.div>

        <motion.div 
          className="grid md:grid-cols-3 gap-px bg-border"
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnceNear}
          variants={staggerContainer}
        >
          {c.audiences.map((audience) => {
            const Icon = resolveIcon(audience.icon);
            return (
              <motion.div
                key={audience.title}
                variants={itemVariant}
                className="bg-background p-6 md:p-8 lg:p-12 transition-colors duration-150 hover:bg-muted/20"
                whileHover={cardHover}
              >
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-muted/50 flex items-center justify-center">
                    <Icon className="h-5 w-5 text-foreground/70" strokeWidth={1.5} />
                  </div>
                  <span className="text-xs font-medium text-primary uppercase tracking-wider">
                    {audience.stage}
                  </span>
                </div>
                <h3 className="text-xl font-display font-medium text-foreground mb-3">
                  {audience.title}
                </h3>
                <p className="text-muted-foreground text-sm leading-relaxed mb-6">
                  {audience.description}
                </p>
                <p className="text-xs text-muted-foreground/60 italic">
                  {audience.notFor}
                </p>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

export default WhoItsFor;
